import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { Button } from "@headlessui/react";
import { ArrowLeftIcon, ArrowPathIcon } from "@heroicons/react/24/solid";
import { useAppSelector } from "../../app/hooks";
import { isDistrictOrSchoolAdmin, isHigherThanDistrictAdmin } from "../../app/userSlice";
import { getParamAsInt } from "../../app/utils";
import { SimpleOption } from "../../components/input/SimpleListbox";
import Select from "../../components/input/Select";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import ButtonWithConfirm from "../../components/ButtonWithConfirm";
import RosterUpload from "./upload/RosterUpload";
import ParseDecisionPanel from "./upload/ParseDecisionPanel";
import ImportProgressBar, { ImportProgress } from "./ImportProgressBar";
import RosterImportSummaryTable from "./summary/RosterImportSummaryTable";
import ImportProgressHistory from "./ImportProgressHistory";
import RosterImportConflictReview from "./conflict/RosterImportConflictReview";
import { isImportError, processInBatches, stepProgress } from "./rosterImportUtils";
import {
	AdminImport,
	AdminImportCourse,
	AdminImportPreparationResult,
	AdminImportResult,
	AdminImportStudent,
	AdminImportTeacher,
	ImportErrorResponse,
	useDistrictOptionsForRosterImportQuery,
	useImportCoursesMutation,
	useImportStudentsMutation,
	useImportTeachersMutation,
	useLazySchoolOptionsForRosterImportQuery,
	usePrepareImportStudentsMutation,
	useUploadFileMutation,
} from "./rosterImportApiSlice";

type AbortFn = () => void;

export default function RosterImport() {
	const { role } = useAppSelector((state) => state.user);
	const [queryParams] = useSearchParams();
	const schoolIdParam = getParamAsInt(queryParams, "schoolId");
	const schoolNameParam = queryParams.get("schoolName");
	const districtIdParam = getParamAsInt(queryParams, "districtId");
	const navigate = useNavigate();

	const DEFAULT_BATCH_SIZE = 10;

	const [schoolId, setSchoolId] = useState(schoolIdParam);
	const [schoolName, setSchoolName] = useState(schoolNameParam);

	const [districtOptions, setDistrictOptions] = useState<SimpleOption[]>([]);
	const [selectedDistrict, setSelectedDistrict] = useState(districtIdParam ?? 0);
	const [schoolOptions, setSchoolOptions] = useState<SimpleOption[]>([]);

	const [error, setError] = useState("");

	const [fileName, setFileName] = useState("");
	const [file, setFile] = useState<File>();

	const [uploadIsInProgress, setUploadIsInProgress] = useState(false);
	const [importIsInProgress, setImportIsInProgress] = useState(false);
	const [progress, setProgress] = useState<ImportProgress>(); // which phase of the import is currently in progress

	const [studentsWithConflicts, setStudentsWithConflicts] = useState<AdminImportStudent[]>();
	const [studentsToImport, setStudentsToImport] = useState<AdminImportStudent[]>();
	const [coursesToImport, setCoursesToImport] = useState<AdminImportCourse[]>();
	const [teachersToImport, setTeachersToImport] = useState<AdminImportTeacher[]>();

	const [importCoursesResults, setImportCoursesResults] = useState<AdminImportResult>();
	const [importTeachersResults, setImportTeachersResults] = useState<AdminImportResult>();
	const [importStudentsResults, setImportStudentsResults] = useState<AdminImportResult>();

	// parsed model + parse gate
	const [importModel, setImportModel] = useState<AdminImport>();
	const [pausedToViewParseResults, setPausedToViewParseResults] = useState(false);

	const [uploadFile] = useUploadFileMutation();
	const [importCourses] = useImportCoursesMutation();
	const [importTeachers] = useImportTeachersMutation();
	const [prepareImportStudents] = usePrepareImportStudentsMutation();
	const [importStudents] = useImportStudentsMutation();

	const setSelectedFile = (f?: File) => {
		setFileName(f?.name ?? "");
		setFile(f);
	};

	// #region Get District and School Filters if needed
	const getDistrictsResult = useDistrictOptionsForRosterImportQuery(
		(role && isDistrictOrSchoolAdmin(role)) || schoolIdParam ? skipToken : undefined
	);
	const districts = getDistrictsResult.data;

	const [callGetSchoolsByDistrict] = useLazySchoolOptionsForRosterImportQuery();

	useEffect(() => {
		if (districts) {
			const options = districts.map((d) => ({ value: d.id, label: d.name }) as SimpleOption);
			options.unshift({ value: 0, label: "None" } as SimpleOption);

			setDistrictOptions(options);
		}
	}, [districts]);

	useEffect(() => {
		if (!schoolIdParam) {
			setSchoolId(undefined);
			callGetSchoolsByDistrict(selectedDistrict == 0 ? undefined : selectedDistrict)
				.unwrap()
				.then((schools) => {
					if (schools) {
						const options = schools.map(
							(s) => ({ value: s.id, label: s.name }) as SimpleOption
						);

						setSchoolOptions(options);
					}
				});
		}
	}, [selectedDistrict]);

	useEffect(() => {
		if (!schoolNameParam && schoolId)
			setSchoolName(schoolOptions.find((s) => s.value == schoolId)?.label ?? "");
	}, [schoolId]);
	// #endregion

	// #region Run the Import Process
	function hasAnyParseIssues(model: AdminImport): boolean {
		const anyBad = (x: { isValid: boolean; errors: string[] }) =>
			!x.isValid || (x.errors?.length ?? 0) > 0;

		return (
			(model.courses?.some(anyBad) ?? false) ||
			(model.teachers?.some(anyBad) ?? false) ||
			(model.students?.some(anyBad) ?? false)
		);
	}

	const applyProgress = (p: ImportProgress) => {
		setProgress({
			message: p.message ?? "",
			phase: p.phase ?? "",
			percent: p.percent ?? null,
			pause: p.pause ?? false,
		});
	};

	//contains step 1
	const runWizard = async () => {
		if (!file) return;
		if (!schoolId || Number.isNaN(schoolId)) return;

		isCancellingRef.current = false;

		setError("");
		setPausedToViewParseResults(false);
		setImportModel(undefined);

		setUploadIsInProgress(true);
		setImportIsInProgress(false);

		try {
			// 1) Upload + parse
			const uploadResult = await runAbortable(uploadFile({ schoolId, file }));

			if (isImportError(uploadResult)) {
				setError(uploadResult.resultMessage);
				return;
			}

			const model: AdminImport = uploadResult;
			setImportModel(model);

			const issuesExist = hasAnyParseIssues(model);

			applyProgress({
				message: `Upload parsed ${issuesExist ? "with issues" : "successfully"}. Review and continue.`,
				phase: "Upload Parsed",
				percent: 0,
				pause: true,
			});

			setPausedToViewParseResults(true);
		} catch (err: any) {
			if (isCancellingRef.current) return;
			if (err?.name === "AbortError" || err?.message === "Aborted") return;

			console.error("Roster import failed", err);
			setError(
				"An unexpected error occurred while importing the roster. Please try again later."
			);
		} finally {
			if (!isCancellingRef.current) {
				setUploadIsInProgress(false);
				setImportIsInProgress(false);
			}
		}
	};

	//create the results for the rows in RosterImportSummaryTable
	const createResultsForStage = (results: AdminImportResult[]) =>
		results.reduce<AdminImportResult>(
			(acc, cur) => ({
				errorMessages: [...acc.errorMessages, ...cur.errorMessages],
				inserts: acc.inserts + cur.inserts,
				updates: acc.updates + cur.updates,
				noChanges: acc.noChanges + cur.noChanges,
			}),
			{ errorMessages: [], inserts: 0, updates: 0, noChanges: 0 }
		);

	//contains steps 2-5
	const continueWizardAfterParseStep = async (model: AdminImport) => {
		if (!schoolId || Number.isNaN(schoolId)) return;

		setPausedToViewParseResults(false);
		setImportIsInProgress(true);

		try {
			// 2) Courses
			const coursesResults = await processInBatches<
				AdminImportCourse,
				AdminImportResult | ImportErrorResponse
			>({
				items: model.courses,
				batchSize: DEFAULT_BATCH_SIZE,
				shouldCancel: () => isCancellingRef.current,
				processor: ({ batch }) => runAbortable(importCourses({ schoolId, data: batch })),
				progressRange: {
					startPercent: stepProgress.importCourses.percent,
					endPercent: stepProgress.importTeachers.percent,
				},
				progressMode: "items",
				onProgress: ({ current, total, percent }) => {
					applyProgress({
						...stepProgress.importCourses,
						message: `${stepProgress.importCourses.message} (${current}/${total})...`,
						percent,
					});
				},
			});

			const coursesErrors = coursesResults.filter(isImportError);
			if (coursesErrors.length) {
				setError(coursesErrors[0].resultMessage);
				return;
			}

			const coursesSuccesses = coursesResults.filter(
				(r): r is AdminImportResult => !isImportError(r)
			);
			setImportCoursesResults(createResultsForStage(coursesSuccesses));
			setCoursesToImport(model.courses);

			// 3) Teachers
			const teachersResult = await processInBatches<
				AdminImportTeacher,
				AdminImportResult | ImportErrorResponse
			>({
				items: model.teachers,
				batchSize: DEFAULT_BATCH_SIZE,
				shouldCancel: () => isCancellingRef.current,
				processor: ({ batch }) => runAbortable(importTeachers({ schoolId, data: batch })),
				progressRange: {
					startPercent: stepProgress.importTeachers.percent,
					endPercent: stepProgress.prepareImportStudents.percent,
				},
				progressMode: "items",
				onProgress: ({ current, total, percent }) => {
					applyProgress({
						...stepProgress.importTeachers,
						message: `${stepProgress.importTeachers.message} (${current}/${total})...`,
						percent,
					});
				},
			});

			const teachersErrors = teachersResult.filter(isImportError);
			if (teachersErrors.length) {
				setError(teachersErrors[0].resultMessage);
				return;
			}

			const teachersSuccesses = teachersResult.filter(
				(r): r is AdminImportResult => !isImportError(r)
			);
			setImportTeachersResults(createResultsForStage(teachersSuccesses));
			setTeachersToImport(model.teachers);

			// 4) Prepare students
			const prepareResults = await processInBatches<
				AdminImportStudent,
				ImportErrorResponse | AdminImportPreparationResult
			>({
				items: model.students,
				batchSize: DEFAULT_BATCH_SIZE,
				shouldCancel: () => isCancellingRef.current,
				processor: ({ batch }) =>
					runAbortable(prepareImportStudents({ schoolId, data: batch })),
				progressRange: {
					startPercent: stepProgress.prepareImportStudents.percent,
					endPercent: stepProgress.resolveStudentConflicts.percent,
				},
				progressMode: "items",
				onProgress: ({ current, total, percent }) => {
					applyProgress({
						...stepProgress.prepareImportStudents,
						message: `${stepProgress.prepareImportStudents.message} (${current}/${total})...`,
						percent,
					});
				},
			});

			const prepErrors = prepareResults.filter(isImportError);
			if (prepErrors.length) {
				setError(prepErrors[0].resultMessage);
				return;
			}

			const prepSuccesses = prepareResults.filter(
				(r): r is AdminImportPreparationResult => !isImportError(r)
			);

			const allStudents = prepSuccesses.flatMap((s) => s.students);
			const conflicts = allStudents.filter((s) => s.identifications.length > 0);
			const noConflicts = allStudents.filter((s) => s.identifications.length === 0);

			setStudentsWithConflicts(conflicts);
			setStudentsToImport(noConflicts);

			// Pause for conflict resolution if needed.
			if (conflicts.length > 0) {
				applyProgress(stepProgress.resolveStudentConflicts);
				return;
			}

			// 5) Import students
			setImportStudentsResults(await importStudentsInBatches(schoolId, noConflicts));
			if (isCancellingRef.current) return;

			applyProgress(stepProgress.complete);
		} catch (err: any) {
			setProgress(undefined);

			if (isCancellingRef.current) return;
			if (err?.name === "AbortError" || err?.message === "Aborted") return;

			console.error("Roster import failed", err);
			setError(
				"An unexpected error occurred while importing the roster. Please try again later."
			);
		} finally {
			if (!isCancellingRef.current) {
				setImportIsInProgress(false);
				setUploadIsInProgress(false);
			}
		}
	};

	async function importStudentsInBatches(
		schoolId: number,
		items: AdminImportStudent[]
	): Promise<AdminImportResult> {
		const results = await processInBatches<
			AdminImportStudent,
			ImportErrorResponse | AdminImportResult
		>({
			items,
			batchSize: DEFAULT_BATCH_SIZE,
			shouldCancel: () => isCancellingRef.current,
			processor: ({ batch }) => runAbortable(importStudents({ schoolId, data: batch })),
			progressRange: {
				startPercent: stepProgress.resolveStudentConflicts.percent,
				endPercent: stepProgress.importStudents.percent,
			},
			progressMode: "items",
			onProgress: ({ current, total, percent }) => {
				applyProgress({
					...stepProgress.importStudents,
					message: `${stepProgress.importStudents.message} (${current}/${total})...`,
					percent,
				});
			},
		});

		const errors = results.filter(isImportError);
		if (errors.length) {
			setError(errors[0].resultMessage);
			return {
				errorMessages: errors.map((e) => e.resultMessage),
				inserts: 0,
				updates: 0,
				noChanges: 0,
			};
		}

		const successes = results.filter((r): r is AdminImportResult => !isImportError(r));

		return successes.reduce<AdminImportResult>(
			(acc, cur) => ({
				errorMessages: [...acc.errorMessages, ...cur.errorMessages],
				inserts: acc.inserts + cur.inserts,
				updates: acc.updates + cur.updates,
				noChanges: acc.noChanges + cur.noChanges,
			}),
			{ errorMessages: [], inserts: 0, updates: 0, noChanges: 0 }
		);
	}

	const manuallyTriggerImportStudentsStep = async (resolvedStudents: AdminImportStudent[]) => {
		if (!schoolId || Number.isNaN(schoolId)) return;

		const merged = (() => {
			const base = studentsToImport ?? [];
			const byRow = new Map(base.map((s) => [s.row, s]));
			for (const s of resolvedStudents) byRow.set(s.row, s);
			return Array.from(byRow.values());
		})();

		setStudentsToImport(merged);
		setStudentsWithConflicts(undefined);

		setImportStudentsResults(await importStudentsInBatches(schoolId, merged));
		if (isCancellingRef.current) return;

		applyProgress(stepProgress.complete);
		setUploadIsInProgress(false);
		setImportIsInProgress(false);
	};
	// #endregion

	// #region Reset or cancel the process
	const resetWizardState = ({ resetFile = true }: { resetFile?: boolean } = {}) => {
		setStudentsWithConflicts(undefined);
		setStudentsToImport(undefined);
		setCoursesToImport(undefined);
		setTeachersToImport(undefined);

		setImportCoursesResults(undefined);
		setImportTeachersResults(undefined);
		setImportStudentsResults(undefined);

		setImportModel(undefined);
		setPausedToViewParseResults(false);

		setProgress(undefined);

		setUploadIsInProgress(false);
		setImportIsInProgress(false);

		setError("");

		if (resetFile) {
			setSelectedFile(undefined);
		}
	};

	const cancelWizard = () => {
		isCancellingRef.current = true;
		abortAllRequests();
		resetWizardState({ resetFile: false });
		isCancellingRef.current = false;
	};
	// #endregion

	// #region Abort Requests
	// Track active RTK Query requests so we can abort them on Cancel.
	const abortersRef = useRef(new Set<AbortFn>());
	const isCancellingRef = useRef(false);

	const trackAborter = (abort: AbortFn) => {
		abortersRef.current.add(abort);
	};

	const abortAllRequests = () => {
		abortersRef.current.forEach((a) => a());
		abortersRef.current.clear();
	};

	const runAbortable = async <T,>(req: { abort: AbortFn; unwrap: () => Promise<T> }) => {
		trackAborter(req.abort);
		try {
			return await req.unwrap();
		} finally {
			abortersRef.current.delete(req.abort);
		}
	};
	// #endregion

	const onBackToSchoolClicked = () => {
		navigate(`/schools/edit?schoolId=${schoolId}`);
	};

	// show different parts of the process
	const showParseDecisionPanel = pausedToViewParseResults && !!importModel;
	const conflictResolutionRequired = (studentsWithConflicts?.length ?? 0) > 0;
	const showImportSummary =
		progress?.phase === stepProgress.complete.phase &&
		!!importCoursesResults &&
		!!importTeachersResults &&
		!!importStudentsResults;

	return (
		<section>
			<h1>Roster Import</h1>

			{/* Upload */}
			{!progress && (
				<div className="mt-6 flex flex-col">
					<div className="w-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-4">
						{schoolIdParam ? (
							<h2>School: {schoolName}</h2>
						) : (
							<div className="flex gap-4 items-end mb-4">
								{districtOptions.length > 0 &&
									role &&
									isHigherThanDistrictAdmin(role) && (
										<Select
											value={selectedDistrict}
											setValue={(v) => setSelectedDistrict(v as number)}
											label="District"
											simpleListBoxId="districtFilter"
											options={districtOptions}
											simpleListBoxClasses="w-96"
										/>
									)}
								<Select
									value={schoolId}
									setValue={(v) => setSchoolId(v as number)}
									label="School"
									simpleListBoxId="schoolFilter"
									prompt="Pick a school..."
									options={schoolOptions}
									simpleListBoxClasses="w-96"
								/>
							</div>
						)}
						<AlertMessage
							type={AlertLevel.Error}
							message={error}
							showIcon={true}
							onClear={() => setError("")}
						/>
						<RosterUpload
							fileName={fileName}
							onFileSelected={setSelectedFile}
							onUpload={runWizard}
							isLoading={uploadIsInProgress}
							disableUpload={!schoolId}
						/>
					</div>
				</div>
			)}

			{/* Wizard */}
			{progress && (
				<div className="mt-6 flex flex-col">
					<div className="w-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-4">
						<div className="flex justify-between">
							<div className="flex flex-col">
								<h2>School: {schoolName}</h2>
								<span className="text-sm text-gray-600">File: {fileName}</span>
							</div>
							{progress.phase === stepProgress.complete.phase && (
								<Button
									type="button"
									className="btn btn-neutral h-fit"
									onClick={() => resetWizardState()}>
									<span className="flex items-center">
										<ArrowPathIcon className="size-4 mr-2" aria-hidden="true" />
										Restart
									</span>
								</Button>
							)}
						</div>

						<AlertMessage
							type={AlertLevel.Error}
							message={error}
							showIcon={true}
							onClear={() => setError("")}
						/>

						{/* Progress Displays */}
						<ImportProgressBar progress={progress} />

						{!showParseDecisionPanel && (
							<ImportProgressHistory
								progress={progress}
								includeActionNeededStep={
									conflictResolutionRequired || showParseDecisionPanel
								}
							/>
						)}

						{/* Parsing Panel View */}
						{showParseDecisionPanel && importModel && (
							<ParseDecisionPanel importModel={importModel} />
						)}

						{/* Conflict Resolution for Students */}
						{conflictResolutionRequired && studentsWithConflicts && (
							<RosterImportConflictReview
								studentsWithConflicts={studentsWithConflicts}
								handleResolveConflicts={manuallyTriggerImportStudentsStep}
							/>
						)}

						{/* Summary */}
						{showImportSummary && (
							<div className="space-y-2 overflow-y-hidden">
								<h2>Import Summary</h2>
								<p className="text-sm text-gray-600 mb-2">
									NOTE: Please note that any rows that had parsing issues in the
									Roster Analysis were not processed during the upload. Similarly,
									any student or teacher that had errors with their account being
									created or updated, were not attempted to be registered in any
									courses.
								</p>
								<RosterImportSummaryTable
									importCoursesResults={importCoursesResults}
									importTeachersResults={importTeachersResults}
									importStudentsResults={importStudentsResults}
									coursesToImport={coursesToImport ?? []}
									teachersToImport={teachersToImport ?? []}
									studentsToImport={studentsToImport ?? []}
								/>
							</div>
						)}

						<div className="flex items-end justify-end w-full gap-2">
							{progress.pause && (
								<ButtonWithConfirm
									message="Are you sure you want to cancel the roster import? Completed steps have already been saved. Any steps still in progress or not yet started may not be saved."
									confirmLabel="Yes"
									cancelLabel="No"
									onConfirm={cancelWizard}>
									Cancel
								</ButtonWithConfirm>
							)}

							{showParseDecisionPanel && (
								<Button
									type="button"
									className="btn btn-primary"
									disabled={importIsInProgress || !importModel}
									onClick={async () => {
										if (!importModel) return;
										setPausedToViewParseResults(false);
										setError("");
										setImportIsInProgress(true);
										await continueWizardAfterParseStep(importModel);
										setImportIsInProgress(false);
									}}>
									Continue
								</Button>
							)}
							{progress.phase === stepProgress.complete.phase &&
								!showParseDecisionPanel && (
									<Button
										type="button"
										className="btn btn-neutral"
										disabled={importIsInProgress}
										onClick={onBackToSchoolClicked}>
										<span className="flex items-center">
											<ArrowLeftIcon
												className="size-4 mr-2"
												aria-hidden="true"
											/>
											Back to School
										</span>
									</Button>
								)}
						</div>
					</div>
				</div>
			)}
		</section>
	);
}
